import React from "react";

// region Imports - External libraries
import { Checkbox, Switch, TextField } from "unform-material-ui";
import { Grid, FormControlLabel, CircularProgress, Tooltip, IconButton } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
// endregion Imports - External libraries
// region Imports - Languages
import { ManagementMessages, GlobalMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared / Utils
import { Status } from "@shared/types/status.enum";
import { User } from "@shared/interfaces/user.interface";
// endregion Imports - Shared / Utils

interface UserPersonalDataProps {
  t: (key: string) => string;
  modalType: "details" | "update" | "register";
  userDetails: User;
  formRef: any;
  loadingValidateFieldEmail: boolean;
  activeCheckbox: boolean;
  setActiveCheckbox: (val: boolean) => void;
  dailyReportCheckbox: boolean;
  setDailyReportCheckbox: (val: boolean) => void;
  weeklyReportCheckbox: boolean;
  setWeeklyReportCheckbox: (val: boolean) => void;
  dailyCanReportCheckbox: boolean;
  setDailyCanReportCheckbox: (val: boolean) => void;
  weeklyCanReportCheckbox: boolean;
  setWeeklyCanReportCheckbox: (val: boolean) => void;
  adminCheckbox: boolean;
  setAdminCheckbox: (val: boolean) => void;
  validateEmail: (email: string) => void;
  defineValueAccordingModalType: (detail: any, update: any, register: any) => any;
  getHelperTextToTelegram: () => string;
  screenPlatform: string;
}
const UserPersonalData: React.FC<UserPersonalDataProps> = ({
  t,
  modalType,
  userDetails,
  formRef,
  loadingValidateFieldEmail,
  activeCheckbox,
  setActiveCheckbox,
  dailyReportCheckbox,
  setDailyReportCheckbox,
  weeklyReportCheckbox,
  setWeeklyReportCheckbox,
  dailyCanReportCheckbox,
  setDailyCanReportCheckbox,
  weeklyCanReportCheckbox,
  setWeeklyCanReportCheckbox,
  adminCheckbox,
  setAdminCheckbox,
  validateEmail,
  defineValueAccordingModalType,
  getHelperTextToTelegram,
  screenPlatform
}) => {
  console.log("Super admin: ", userDetails?.super_admin);

  return (
    <>
      <Grid item xs={12} md={12} lg={12} className="checkboxes">
        <FormControlLabel
          className="checkbox"
          style={{ gridColumn: "1 / -1" }}
          control={(
            <Checkbox
              onChange={(event) => setActiveCheckbox(event.target.checked)}
              checked={activeCheckbox}
              name="active"
              disabled={modalType === "details"}
            />
          )}
          label={t(ManagementMessages.detailsActiveUser)}
        />
        <span className="checkbox-group-label">{t(ManagementMessages.detailsReports)}</span>
        <FormControlLabel
          className="checkbox"
          control={(
            <Checkbox
              onChange={(event) => setDailyReportCheckbox(event.target.checked)}
              checked={dailyReportCheckbox}
              name="receive_daily_report"
              disabled={modalType === "details"}
            />
          )}
          label={t(GlobalMessages.daily)}
        />
        <FormControlLabel
          className="checkbox"
          control={(
            <Checkbox
              onChange={(event) => setWeeklyReportCheckbox(event.target.checked)}
              checked={weeklyReportCheckbox}
              name="receive_weekly_report"
              disabled={modalType === "details"}
            />
          )}
          label={t(GlobalMessages.weekly)}
        />
        <span className="checkbox-group-label">{t(ManagementMessages.detailsCanReports)}</span>
        <FormControlLabel
          className="checkbox"
          control={(
            <Checkbox
              onChange={(event) => setDailyCanReportCheckbox(event.target.checked)}
              checked={dailyCanReportCheckbox}
              name="receive_daily_can_report"
              disabled={modalType === "details"}
            />
          )}
          label={t(GlobalMessages.daily)}
        />
        <FormControlLabel
          className="checkbox"
          control={(
            <Checkbox
              onChange={(event) => setWeeklyCanReportCheckbox(event.target.checked)}
              checked={weeklyCanReportCheckbox}
              name="receive_weekly_can_report"
              disabled={modalType === "details"}
            />
          )}
          label={t(GlobalMessages.weekly)}
        />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <TextField
          className="name"
          label={t(ManagementMessages.detailsFullname)}
          margin="dense"
          variant="outlined"
          name="name"
          InputLabelProps={{
            shrink: formRef.current?.getFieldValue("name")?.length > 0 ? true : undefined
          }}
          disabled={modalType === "details"}
          helperText={t(GlobalMessages.required)}
          defaultValue={defineValueAccordingModalType(userDetails.name, userDetails.name, "")}
        />
      </Grid>
      <Grid item xs={12} md={8} lg={8}>
        <TextField
          className="email"
          label={t(ManagementMessages.detailsEmail)}
          margin="dense"
          variant="outlined"
          name="email"
          type="email"
          InputLabelProps={{
            shrink: formRef.current?.getFieldValue("email")?.length > 0 ? true : undefined
          }}
          InputProps={{
            endAdornment: (
              <>
                {loadingValidateFieldEmail ? <CircularProgress color="inherit" size={20} /> : null}
              </>
            )
          }}
          disabled={modalType === "details"}
          helperText={t(GlobalMessages.required)}
          defaultValue={defineValueAccordingModalType(userDetails.email, userDetails.email, "")}
          onBlur={() => validateEmail(formRef.current?.getFieldValue("email"))}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={4} className="admin">
        <FormControlLabel
          control={(
            <Switch
              checked={adminCheckbox}
              onChange={(event) => setAdminCheckbox(event.target.checked)}
              name="admin"
              disabled={modalType === "details" || userDetails?.super_admin}
              color="primary"
            />
          )}
          label={t(ManagementMessages.detailsAdmin)}
          labelPlacement="start"
        />
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <TextField
          className="password"
          label={t(ManagementMessages.detailsPassword)}
          margin="dense"
          variant="outlined"
          name="password"
          type="password"
          autoComplete="new-password"
          disabled={modalType === "details"}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <TextField
          className="passwordConfirm"
          label={t(ManagementMessages.detailsConfirmPassword)}
          margin="dense"
          variant="outlined"
          name="passwordConfirm"
          type="password"
          disabled={modalType === "details"}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={6}>
        <TextField
          className="telegram_id"
          label={t(ManagementMessages.detailsTelegramId)}
          margin="dense"
          variant="outlined"
          name="telegram_id"
          InputLabelProps={{
            shrink: formRef.current?.getFieldValue("telegram_id")?.length > 0 ? true : undefined
          }}
          disabled={modalType === "details"}
          defaultValue={defineValueAccordingModalType(userDetails.telegram_id, userDetails.telegram_id, "")}
        />
      </Grid>
      <Grid container alignItems="center" xs={12} md={2} lg={4}>
        {
          screenPlatform === "mobile" ? (
            <a
              href="https://www.notion.so/topconsuite/Documentation-Alerts-TELEGRAM-Integration-7f29e2aaf29b4b8cbcd6a007ec696b3c"
              className="mobileHelperText"
            >
              {getHelperTextToTelegram()}
            </a>
          ) : (
            <Tooltip
              title={(
                <p className="tooltipText" style={{ fontSize: "12px" }}>
                  {getHelperTextToTelegram()}
                </p>
              )}
            >
              <IconButton onClick={() => {
                window.open(
                  "https://www.notion.so/topconsuite/Documentation-Alerts-TELEGRAM-Integration-7f29e2aaf29b4b8cbcd6a007ec696b3c",
                  "_blank"
                );
              }}
              >
                <InfoIcon
                  height="100%"
                  color={
                    (
                      userDetails.sent_alerts_history
                      && userDetails.sent_alerts_history?.length > 0
                      && userDetails.sent_alerts_history[0].status === Status.ERROR)
                      ? "error"
                      : "disabled"
                  }
                />
              </IconButton>
            </Tooltip>
          )
        }
      </Grid>
    </>
  );
};

export default UserPersonalData;
